import { ConfigContext } from '@/Contexts/ConfigContext';
import { AdminApplicationExportEmailRequestDto } from '@/models/Admin/Emails/EmailRequestDtos';
import { ApplicationTypes } from '@/models/ApplicationType';
import { ProgramNames } from '@/models/Program';
import { USCountryId } from '@/utils/constants';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import AdminApplicationExportForm from './AdminApplicationExportForm';
import { AdminApplicationExportSubmitState } from './types';
import { isEmail } from '@/utils/emailUtil';

type Props = {
  onStateChange: React.Dispatch<React.SetStateAction<AdminApplicationExportSubmitState>>;
  resetSignal: number;
};

const programOptions = Object.values(ProgramNames).filter(
  (value): value is ProgramNames => typeof value === 'number'
);

const applicationTypeOptions = Object.values(ApplicationTypes).filter(
  (value): value is ApplicationTypes => typeof value === 'number'
);

const createInitialForm = (): AdminApplicationExportEmailRequestDto => ({
  Email: '',
  Program: ProgramNames.TreeCityUSA,
  ApplicationType: ApplicationTypes.Standard,
  Year: new Date().getFullYear(),
  IncludeQuestionResponses: false,
  StateId: null,
  CountyId: null,
  CountryId: null,
  PartnerIds: [],
});

const createEmptySubmitState = (): AdminApplicationExportSubmitState => ({
  request: createInitialForm(),
  isValid: false,
});

const AdminApplicationExportSection: React.FC<Props> = ({ onStateChange, resetSignal }) => {
  const { allStates, allCounties, allCountries } = useContext(ConfigContext);

  const [form, setForm] = useState<AdminApplicationExportEmailRequestDto>(createInitialForm());
  const [includeQuestionResponses, setIncludeQuestionResponses] = useState<boolean>(false);
  const [countryIdInput, setCountryIdInput] = useState<string>('');
  const [stateId, setStateId] = useState<string>('');
  const [countyId, setCountyId] = useState<string>('');
  const [partnerIdsInput, setPartnerIdsInput] = useState<string>('');

  const isCountryUS = useMemo(() => {
    const countryId = Number(countryIdInput);
    return Number.isFinite(countryId) && countryId === USCountryId;
  }, [countryIdInput]);

  const countyOptions = useMemo(() => {
    if (stateId.trim() === '') {
      return [];
    }

    return allCounties
      .filter((county) => county.stateId === stateId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allCounties, stateId]);

  const preparedRequest = useMemo((): AdminApplicationExportEmailRequestDto => {
    const partnerIds =
      partnerIdsInput.trim() === ''
        ? []
        : partnerIdsInput
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== '')
            .map((item) => Number(item))
            .filter((id) => Number.isInteger(id) && id > 0);

    return {
      ...form,
      IncludeQuestionResponses: includeQuestionResponses,
      StateId: isCountryUS && stateId !== '' ? stateId : null,
      CountyId: isCountryUS && countyId !== '' ? countyId : null,
      CountryId: countryIdInput === '' ? null : Number(countryIdInput),
      PartnerIds: partnerIds,
    };
  }, [
    countryIdInput,
    countyId,
    form,
    includeQuestionResponses,
    isCountryUS,
    partnerIdsInput,
    stateId,
  ]);

  const isValid =
    preparedRequest.ApplicationType != null &&
    preparedRequest.Program != null &&
    isEmail(preparedRequest.Email ?? '') &&
    Number.isFinite(preparedRequest.Year) &&
    preparedRequest.Year > 0;

  useEffect(() => {
    onStateChange({
      request: preparedRequest,
      isValid,
    });
  }, [isValid, onStateChange, preparedRequest]);

  useEffect(() => {
    setForm(createInitialForm());
    setIncludeQuestionResponses(false);
    setCountryIdInput('');
    setStateId('');
    setCountyId('');
    setPartnerIdsInput('');
    onStateChange(createEmptySubmitState());
  }, [onStateChange, resetSignal]);

  return (
    <AdminApplicationExportForm
      adminApplicationExportForm={form}
      setAdminApplicationExportForm={setForm}
      programOptions={programOptions}
      applicationTypeOptions={applicationTypeOptions}
      adminIncludeQuestionResponses={includeQuestionResponses}
      setAdminIncludeQuestionResponses={setIncludeQuestionResponses}
      adminCountryIdInput={countryIdInput}
      setAdminCountryIdInput={setCountryIdInput}
      isAdminCountryUS={isCountryUS}
      adminStateId={stateId}
      setAdminStateId={setStateId}
      adminCountyId={countyId}
      setAdminCountyId={setCountyId}
      adminCountyOptions={countyOptions}
      adminPartnerIdsInput={partnerIdsInput}
      setAdminPartnerIdsInput={setPartnerIdsInput}
      allCountries={allCountries}
      allStates={allStates}
    />
  );
};

export default AdminApplicationExportSection;
